import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Home from './components/Home';
import ComposeMail from './components/ComposeMail';
import Inbox from './components/Inbox';
import Drafts from './components/Drafts';
import Sent from './components/Sent';
import Scheduled from './components/Scheduled';
import Starred from './components/Starred';
import Important from './components/Important';
import Spam from './components/Spam';
import Trash from './components/Trash';
import AllMail from './components/AllMail';
import SearchResults from './components/SearchResults';
import Navigation from './components/Navigation';
import Sidebar from './components/Sidebar';
import AdminPanel from './components/AdminPanel';
import GroupMailbox from './components/GroupMailbox';
import ManageAccount from './components/ManageAccount';
import SubscriptionPayment from './components/SubscriptionPayment';
import ChangePassword from './components/ChangePassword';
import Contacts from './components/Contacts';
import AiAssistantFab from './components/AiAssistantFab';
import AtozasMailboxSetup from './components/AtozasMailboxSetup';
import { LabelMails } from './components/labels';
import axiosInstance from './utils/axiosConfig';
import { clearAllMailPasswords, hasMailPassword, setMailPassword } from './utils/mailCredentials';
import { getInstallInstructions, isRunningAsInstalledApp, subscribeInstalledState } from './utils/appInstall';
import { subscribeToPush, unsubscribeFromPush } from './utils/pushNotifications';


/**
 * LoginRoute Component
 * Handles login route with support for adding another account
 */
const LoginRoute = ({ currentUser, onLogin, isAddingAccount }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const addAccount = params.get('addAccount') === 'true';
  const targetEmail = params.get('email');
  const isSwitchingAccount =
    !!currentUser &&
    !!targetEmail &&
    targetEmail.toLowerCase() !== currentUser.email?.toLowerCase();

  // Stay in the app until logout — do not show login while a session exists.
  if (currentUser && !addAccount && !isSwitchingAccount) {
    return <Navigate to="/inbox" replace />;
  }

  const isRegister = location.pathname === '/register';
  return (
    <Login
      onLogin={onLogin}
      isAddingAccount={addAccount}
      initialMode={isRegister ? 'register' : 'login'}
    />
  );
};

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [allAccounts, setAllAccounts] = useState([]);
  const [showInstallPopup, setShowInstallPopup] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installHint, setInstallHint] = useState('');
  const [installSteps, setInstallSteps] = useState(() => getInstallInstructions().steps);
  const [canNativeInstall, setCanNativeInstall] = useState(false);
  const [isAppInstalled, setIsAppInstalled] = useState(isRunningAsInstalledApp);
  const deferredPromptRef = useRef(null);
  // On mobile, sidebar should be hidden by default
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Check if mobile on initial load
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  const closeMobileSidebar = useCallback(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setSidebarCollapsed(true);
    }
  }, []);
  const [loading, setLoading] = useState(true);
  const [atozasNeedsSetup, setAtozasNeedsSetup] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const adminEmails = useMemo(() => {
    const configured =
      process.env.REACT_APP_ADMIN_EMAILS ||
      process.env.REACT_APP_ADMIN_EMAIL ||
      'admin@atozasmail.in';
    return configured
      .split(',')
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean);
  }, []);
  const isAdmin = adminEmails.includes(currentUser?.email?.toLowerCase() || '');

  const getOrCreateDeviceId = useCallback(() => {
    const key = 'deviceId';
    let deviceId = localStorage.getItem(key);
    if (!deviceId) {
      const randomPart = Math.random().toString(36).slice(2, 12);
      deviceId = `dev_${Date.now()}_${randomPart}`;
      localStorage.setItem(key, deviceId);
    }
    return deviceId;
  }, []);

  /**
   * Load account list from database
   */
  const loadAccountsFromDatabase = useCallback(async () => {
    try {
      const deviceId = getOrCreateDeviceId();
      const response = await axiosInstance.get('/api/users/accounts', {
        params: { deviceId },
      });
      if (response.data.success) {
        setAllAccounts(response.data.data || []);
      } else {
        setAllAccounts([]);
      }
    } catch (error) {
      console.error('Error loading accounts from database:', error);
      setAllAccounts([]);
    }
  }, [getOrCreateDeviceId]);

  /**
   * Check authentication status
   * Verifies JWT token and gets current user
   */
  /**
   * On startup, if there is no local Mail session, check whether an ATOZAS SSO
   * session exists. GET /auth/atozas/me resolves one of:
   *  - Mail account exists  -> restore existing JWT + log in (no login screen).
   *  - ATOZAS authed, no box -> flag first-time mailbox setup.
   *  - Not authenticated     -> do nothing (normal login screen).
   * This never redirects the user back to ATOZAS on its own.
   */
  const tryAtozasSession = useCallback(async () => {
    try {
      const deviceId = getOrCreateDeviceId();
      const { data } = await axiosInstance.get('/auth/atozas/me', { params: { deviceId } });
      if (!data || !data.enabled || !data.authenticated) return false;

      if (!data.needsMailbox && data.token && data.user) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.mailPassword) setMailPassword(data.mailPassword, data.user.email);
        setCurrentUser(data.user);
        await loadAccountsFromDatabase();
        return true;
      }

      if (data.needsMailbox) {
        setAtozasNeedsSetup(true);
        return true;
      }
    } catch (error) {
      // SSO disabled or not signed in — fall back to normal login.
    }
    return false;
  }, [getOrCreateDeviceId, loadAccountsFromDatabase]);

  const checkAuth = useCallback(async () => {
    const path = window.location.pathname || '';
    const isAtozasHandoff = path === '/auth/atozas/finish' || path === '/auth/atozas/setup';

    // IdP product-card / OIDC callback lands on /auth/atozas/finish. Do not
    // validate a stale JWT here — leave handoff to AtozasMailboxSetup. Only
    // clear the logout guard; wiping token/user here raced with /me minting a
    // fresh JWT and caused login ↔ inbox refresh loops until hard refresh.
    if (isAtozasHandoff) {
      try {
        localStorage.removeItem('atozas_logged_out');
        sessionStorage.removeItem('atozas_logged_out');
      } catch (_e) {
        /* storage unavailable */
      }
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      // Respect an explicit or expiry logout across refreshes. ATOZAS may still
      // have an SSO cookie if its logout endpoint was unavailable, so do not
      // silently mint another app JWT until the user starts a new login.
      if (
        localStorage.getItem('atozas_logged_out') === '1' ||
        sessionStorage.getItem('atozas_logged_out') === '1'
      ) {
        // Migrate the previous tab-only guard so hard refreshes and restarts
        // cannot restore a still-active ATOZAS SSO session.
        localStorage.setItem('atozas_logged_out', '1');
        sessionStorage.removeItem('atozas_logged_out');
        setLoading(false);
        return;
      }
      // No local Mail session — attempt to restore an ATOZAS SSO session.
      await tryAtozasSession();
      setLoading(false);
      return;
    }

    try {
      // Verify token and get current user
      const response = await axiosInstance.get('/api/auth/me');
      if (response.data.success) {
        const user = response.data.data.user;
        localStorage.setItem('user', JSON.stringify(user));
        setCurrentUser(user);
        await loadAccountsFromDatabase();

      } else {
        // Token invalid — try ATOZAS SSO cookie before forcing the login screen.
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearAllMailPasswords();
        setAllAccounts([]);
        await tryAtozasSession();
      }
    } catch (error) {
      // Token expired/invalid. Do NOT set atozas_logged_out — that flag is only
      // for explicit logout. Expiry should restore from the SSO session cookie
      // so users are not stuck until they clear browser cookies.
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      clearAllMailPasswords();
      setAllAccounts([]);
      await tryAtozasSession();
    } finally {
      setLoading(false);
    }
  }, [loadAccountsFromDatabase, tryAtozasSession]);

  /**
   * Check if user is authenticated on mount
   * Verify token from localStorage
   */
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Register this device for push notifications once logged in.
  // Silent: only re-uses an already-granted permission (the QuickSettings
  // toggle handles the explicit permission request via a user gesture).
  useEffect(() => {
    if (!currentUser) return;
    subscribeToPush({ requestPermission: false }).catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    if (!currentUser && !loading) {
      root.classList.add('app-public-scroll');
      body.classList.add('app-public-scroll');
    } else {
      root.classList.remove('app-public-scroll');
      body.classList.remove('app-public-scroll');
    }
    return () => {
      root.classList.remove('app-public-scroll');
      body.classList.remove('app-public-scroll');
    };
  }, [currentUser, loading]);

  useEffect(() => {
    // Never show any install UI when already running as an installed app
    // (installed PWA, Electron EXE, or Android APK / Capacitor / TWA).
    if (isRunningAsInstalledApp()) {
      setIsAppInstalled(true);
      setShowInstallPopup(false);
      return undefined;
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredPromptRef.current = event;
      setDeferredPrompt(event);
      setCanNativeInstall(true);
      setInstallHint('');
      setInstallSteps([]);
    };

    const showManualInstallPopup = () => {
      const instructions = getInstallInstructions();
      setInstallSteps(instructions.steps);
      setInstallHint('');
      setShowInstallPopup(true);
    };

    // Triggered by the "Download App" button in the sidebar. Install directly
    // via the captured browser prompt; fall back to the instructions popup.
    const handleOpenInstallPrompt = async () => {
      if (isRunningAsInstalledApp()) return;
      const promptEvent = deferredPromptRef.current;
      if (promptEvent) {
        try {
          await promptEvent.prompt();
          const choice = await promptEvent.userChoice;
          if (choice.outcome === 'accepted') {
            setIsAppInstalled(true);
          }
        } catch (error) {
          console.error('Install prompt error:', error);
          showManualInstallPopup();
        }
        deferredPromptRef.current = null;
        setDeferredPrompt(null);
        setCanNativeInstall(false);
        setShowInstallPopup(false);
        return;
      }
      showManualInstallPopup();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('openInstallPrompt', handleOpenInstallPrompt);
    const unsubscribeInstalled = subscribeInstalledState(() => {
      setIsAppInstalled(true);
      setShowInstallPopup(false);
      deferredPromptRef.current = null;
      setDeferredPrompt(null);
      setCanNativeInstall(false);
    });

    const installDismissed = sessionStorage.getItem('installPopupDismissed');
    if (!installDismissed) {
      const instructions = getInstallInstructions();
      setInstallSteps(instructions.steps);
      setShowInstallPopup(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('openInstallPrompt', handleOpenInstallPrompt);
      unsubscribeInstalled();
    };
  }, []);

  const handleCloseInstallPopup = () => {
    sessionStorage.setItem('installPopupDismissed', '1');
    setShowInstallPopup(false);
  };

  const handleInstallApp = async () => {
    const promptEvent = deferredPromptRef.current || deferredPrompt;
    if (!promptEvent) return;

    try {
      await promptEvent.prompt();
      const choiceResult = await promptEvent.userChoice;
      if (choiceResult.outcome === 'accepted') {
        setShowInstallPopup(false);
        setIsAppInstalled(true);
      }
    } catch (error) {
      console.error('Install prompt error:', error);
      const instructions = getInstallInstructions();
      setInstallSteps(instructions.steps);
      setCanNativeInstall(false);
    }

    deferredPromptRef.current = null;
    setDeferredPrompt(null);
    setCanNativeInstall(false);
  };

  /**
   * Handle successful login
   * User and active token are stored in localStorage by Login component
   */
  const handleLogin = async (user) => {
    // A fresh, user-initiated login allows SSO restoration again next time.
    localStorage.removeItem('atozas_logged_out');
    sessionStorage.removeItem('atozas_logged_out');
    setAtozasNeedsSetup(false);
    setCurrentUser(user);
    await loadAccountsFromDatabase();
  };

  const handleProfileUpdate = (user) => {
    setCurrentUser(user);
    localStorage.setItem('user', JSON.stringify(user));
    setAllAccounts((prev) =>
      prev.map((account) =>
        account.email === user.email ? { ...account, ...user } : account
      )
    );
  };

  /**
   * Handle logout
   * Clear token and user from localStorage
   */
  const handleLogout = async () => {
    // One-shot guard: block the automatic ATOZAS re-login on the immediate
    // post-logout page load (see checkAuth). Set before anything else so it
    // survives even if the network logout below is slow.
    try {
      localStorage.setItem('atozas_logged_out', '1');
      sessionStorage.setItem('atozas_logged_out', '1');
    } catch (_e) {
      /* sessionStorage unavailable — ignore */
    }
    unsubscribeFromPush().catch(() => {});
    // Destroy the server-side ATOZAS SSO session (and provider tokens) FIRST and
    // AWAIT it. Otherwise the caller's immediate window.location redirect cancels
    // this in-flight request, the session survives, and the next /me silently
    // re-authenticates the user straight back into the Inbox. Bounded by a short
    // timeout so a slow/unavailable endpoint never blocks logout.
    try {
      await axiosInstance.post('/auth/logout', null, { timeout: 5000 });
    } catch (_e) {
      /* best-effort — proceed with local logout regardless */
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearAllMailPasswords();
    setCurrentUser(null);
    setAllAccounts([]);
    setAtozasNeedsSetup(false);
    setSelectedDraft(null);
  };

  /**
   * Handle switching to another account already logged in on this device
   */
  const handleSwitchAccount = async (accountEmail) => {
    const targetEmail = String(accountEmail || '').trim().toLowerCase();
    if (!targetEmail || targetEmail === currentUser?.email?.toLowerCase()) return;

    try {
      const deviceId = getOrCreateDeviceId();
      const response = await axiosInstance.post('/api/auth/switch-account', {
        targetEmail,
        deviceId,
      });

      if (response.data.success) {
        const { user, token } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setCurrentUser(user);
        await loadAccountsFromDatabase();

        if (hasMailPassword(user.email)) {
          window.location.replace('/inbox');
        } else {
          window.location.replace(`/login?email=${encodeURIComponent(user.email)}`);
        }
        return;
      }

      window.location.replace(`/login?email=${encodeURIComponent(targetEmail)}&addAccount=true`);
    } catch (error) {
      console.error('Error switching account:', error);
      window.location.replace(`/login?email=${encodeURIComponent(targetEmail)}&addAccount=true`);
    }
  };

  /**
   * Handle deleting an account
   */
  const handleDeleteAccount = (accountEmail) => {
    if (accountEmail === currentUser?.email) return;
    setAllAccounts(prev => prev.filter(account => account.email !== accountEmail));
  };

  /**
   * Handle loading a draft for editing
   */
  const handleLoadDraft = (draft) => {
    setSelectedDraft(draft);
  };

  /**
   * Handle clearing draft after sending
   */
  const handleClearDraft = () => {
    setSelectedDraft(null);
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className={`App${currentUser ? ' app-authenticated' : ' app-public'}`}>
        {showInstallPopup && !isAppInstalled && (
          <div className="install-popup-overlay">
            <div className="install-popup-card">
              <h3>Install MailClient</h3>
              <p>Install this app for faster access and a more streamlined experience.</p>
              {installSteps.length > 0 && (
                <ol className="install-popup-steps">
                  {installSteps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              )}
              {installHint && <p className="install-popup-hint">{installHint}</p>}
              <div className="install-popup-actions">
                <button className="install-secondary-btn" onClick={handleCloseInstallPopup}>Later</button>
                {canNativeInstall ? (
                  <button className="install-primary-btn" onClick={handleInstallApp}>Install</button>
                ) : (
                  <button className="install-primary-btn" onClick={handleCloseInstallPopup}>Got it</button>
                )}
              </div>
            </div>
          </div>
        )}
        {currentUser && (
          <Navigation 
            currentUser={currentUser} 
            onLogout={handleLogout}
            onSwitchAccount={handleSwitchAccount}
            onDeleteAccount={handleDeleteAccount}
            onProfileUpdate={handleProfileUpdate}
            allAccounts={allAccounts}
            onMenuClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        )}
        {currentUser && !sidebarCollapsed && (
          <button
            type="button"
            className="mobile-sidebar-backdrop"
            onClick={closeMobileSidebar}
            aria-label="Close menu"
          />
        )}
        {!currentUser ? (
          <div className="public-shell">
            <Routes>
              {/* ATOZAS SSO handoff + first-time mailbox creation. */}
              <Route
                path="/auth/atozas/finish"
                element={<AtozasMailboxSetup onLogin={handleLogin} />}
              />
              <Route
                path="/auth/atozas/setup"
                element={<AtozasMailboxSetup onLogin={handleLogin} />}
              />
              <Route
                path="/"
                element={
                  atozasNeedsSetup ? (
                    <Navigate to="/auth/atozas/setup" replace />
                  ) : (
                    <Home currentUser={currentUser} />
                  )
                }
              />
              <Route
                path="/login"
                element={
                  <LoginRoute
                    currentUser={currentUser}
                    onLogin={handleLogin}
                    isAddingAccount={true}
                  />
                }
              />
              <Route
                path="/register"
                element={
                  <LoginRoute
                    currentUser={currentUser}
                    onLogin={handleLogin}
                    isAddingAccount={true}
                  />
                }
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route
                path="*"
                element={
                  <Navigate to={atozasNeedsSetup ? '/auth/atozas/setup' : '/'} replace />
                }
              />
            </Routes>
          </div>
        ) : (
        <div className="main-content">
          <Sidebar
            collapsed={sidebarCollapsed}
            currentUser={currentUser}
            onNavItemClick={closeMobileSidebar}
          />
          <div className="content-area">
            {currentUser ? <AiAssistantFab currentUser={currentUser} /> : null}
            <Routes>
              <Route
                path="/"
                element={<Navigate to="/inbox" replace />}
              />
              {/* Already authenticated — SSO handoff paths just go to the Inbox. */}
              <Route path="/auth/atozas/finish" element={<Navigate to="/inbox" replace />} />
              <Route path="/auth/atozas/setup" element={<Navigate to="/inbox" replace />} />
              <Route
                path="/login"
                element={
                  <LoginRoute
                    currentUser={currentUser}
                    onLogin={handleLogin}
                    isAddingAccount={false}
                  />
                }
              />
              <Route
                path="/register"
                element={
                  <LoginRoute
                    currentUser={currentUser}
                    onLogin={handleLogin}
                    isAddingAccount={false}
                  />
                }
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route
                path="/compose"
                element={
                  currentUser ? (
                    <>
                      <Inbox currentUser={currentUser} />
                      <ComposeMail 
                        currentUser={currentUser} 
                        draft={selectedDraft}
                        onClearDraft={handleClearDraft}
                      />
                    </>
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/inbox" 
                element={
                  currentUser ? (
                    <Inbox currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/drafts" 
                element={
                  currentUser ? (
                    <Drafts 
                      currentUser={currentUser} 
                      onLoadDraft={handleLoadDraft}
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/sent" 
                element={
                  currentUser ? (
                    <Sent currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route
                path="/contacts"
                element={
                  currentUser ? (
                    <Contacts currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/labels/:labelId"
                element={
                  currentUser ? (
                    <LabelMails />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route 
                path="/starred" 
                element={
                  currentUser ? (
                    <Starred currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/important" 
                element={
                  currentUser ? (
                    <Important currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/spam" 
                element={
                  currentUser ? (
                    <Spam currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/trash" 
                element={
                  currentUser ? (
                    <Trash currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/all-mail" 
                element={
                  currentUser ? (
                    <AllMail currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/search" 
                element={
                  currentUser ? (
                    <SearchResults currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/snoozed" 
                element={
                  currentUser ? (
                    <Inbox currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route 
                path="/scheduled" 
                element={
                  currentUser ? (
                    <Scheduled currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } 
              />
              <Route
                path="/groups/:groupId"
                element={
                  currentUser ? (
                    <GroupMailbox currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/admin"
                element={
                  currentUser && isAdmin ? (
                    <AdminPanel currentUser={currentUser} />
                  ) : currentUser ? (
                    <Navigate to="/inbox" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/account"
                element={
                  currentUser ? (
                    <ManageAccount
                      currentUser={currentUser}
                      onProfileUpdate={handleProfileUpdate}
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/account/subscription"
                element={
                  currentUser ? (
                    <SubscriptionPayment currentUser={currentUser} />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/account/password"
                element={
                  currentUser ? (
                    <ChangePassword />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/ai-assistant"
                element={
                  currentUser ? (
                    <Navigate
                      to="/inbox"
                      replace
                      state={{ openAiAssistant: true }}
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
            </Routes>
          </div>
        </div>
        )}
      </div>
    </Router>
  );
}

export default App;
